import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ClipboardListIcon, ShieldCheckIcon, HeartIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { ANIMALS_IMAGES, animalsCopy, animalsBenefits, type Bi } from '../../data/animalsHero';
import { CitizenQuestion, AtnaCard, PetPhone } from './AnimalsCards';
const EASE = [0.23, 1, 0.32, 1] as const;
const BENEFIT_ICONS = {
  compliance: ClipboardListIcon,
  safety: ShieldCheckIcon,
  lost: HeartIcon,
  revenue: BarChart3Icon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 22%)',
  maskImage: 'linear-gradient(to right, transparent 0%, #000 22%)'
};
export function AnimalsHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const c = animalsCopy;
  const buttons = <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-247" data-mp="2d0fzejxd9nxh">
      <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
      location: 'animals_hero',
      cta: 'see_in_action'
    })} className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4F46E5] px-8 py-4 font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-248" data-mp="2d0pp06ei1t2n">
        {pick(c.primary)}
        <ArrowRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-249" data-mp="2rpm5mrumgeev" />
      </Link>
      <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
      location: 'animals_hero',
      cta: 'talk_to_team'
    })} className="inline-flex items-center justify-center rounded-full border-[1.5px] border-[#4F46E5] bg-white px-8 py-4 font-sans text-[16px] font-medium text-[#4F46E5] whitespace-nowrap hover:bg-[#F5F4FE] transition-colors duration-150" data-id="element-250" data-mp="35p54sv5ggff1">
        {pick(c.secondary)}
      </Link>
    </div>;
  return <div className="relative w-full" data-id="element-251" data-mp="36ftqs2yltxr6">
      {/* Desktop photo layer — full bleed on the right, fading into white on the left */}
      <div className="hidden xl:block absolute inset-y-0 right-0 w-[70%] pointer-events-none" aria-hidden="true" data-id="element-252" data-mp="3bf6m7c3jkd5d">
        <img src={ANIMALS_IMAGES.pets} alt="" className="w-full h-full object-cover object-[45%_30%]" style={PHOTO_MASK} data-id="element-253" data-mp="3kds2f0ljjg2z" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/70 to-transparent" data-id="element-254" data-mp="3pdwiipije8so" />
      </div>
      {/* Bottom white wave */}
      <svg className="hidden xl:block absolute bottom-0 left-0 w-full h-[150px] pointer-events-none" viewBox="0 0 1440 150" preserveAspectRatio="none" aria-hidden="true" data-id="element-255" data-mp="3ua4uxv5cnnw5">
        <path d="M0 150 L0 70 C 420 150, 900 150, 1440 20 L1440 150 Z" fill="#FFFFFF" data-id="element-256" data-mp="7yfz1kftvjgg" />
      </svg>

      <div className="relative max-w-[1680px] mx-auto px-6 xl:min-h-[720px]" data-id="element-257" data-mp="bwdrcw4walo2">
        <div className="grid grid-cols-1 xl:grid-cols-[40%_60%] gap-10 xl:gap-0" data-id="element-258" data-mp="gxkx6pnh9as3">
          {/* Left — message */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="min-w-0 pt-6 xl:pt-16 xl:pb-40 relative z-10" data-id="element-259" data-mp="21czj6w4y8g6e">
            <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-260" data-mp="1ngchyh5yr3uo">{pick(c.eyebrow)}</p>
            <h1 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.3rem] leading-[1.02] tracking-tight mt-5" data-id="element-261" data-mp="2k5bvobpbgxu9">
              <span className="block text-[#121A3A]" data-id="element-262" data-mp="2g6cqcpzoaz2a">{pick(c.line1)}</span>
              <span className="block text-[#121A3A]" data-id="element-263" data-mp="2bbyvcooxaty5">{pick(c.line2)}</span>
              <span className="block text-[#4F46E5]" data-id="element-264" data-mp="26ddsehiadw60">{pick(c.line3)}</span>
              <span className="block text-[#4F46E5]" data-id="element-265" data-mp="33tko8murv5ua">{pick(c.line4)}</span>
            </h1>
            <p className="font-sans text-[18px] md:text-[19px] text-[#5B6478] leading-relaxed mt-6 max-w-[560px]" data-id="element-266" data-mp="2z345qoc3gu12">{pick(c.body)}</p>
            <div className="mt-8" data-id="element-267" data-mp="2imjzbbrt7lzp">{buttons}</div>
          </motion.div>

          {/* Right — desktop layered cards */}
          <div className="hidden xl:block relative min-w-0" data-id="element-268" data-mp="2no9d2z6f2drh">
            <div className="absolute top-[70px] left-[42%] w-[46%] z-10" data-id="element-269" data-mp="29ngdw1566ui1">
              <CitizenQuestion isFR={isFR} data-id="element-270" data-mp="2em026878kidk" />
            </div>
            <svg className="absolute top-[150px] left-[47%] w-[60px] h-[80px] z-10" viewBox="0 0 60 80" fill="none" aria-hidden="true" data-id="element-271" data-mp="2emjk3owvz6rl">
              <path d="M4 2 V30 Q4 40 14 40 H46 Q56 40 56 50 V78" stroke="#6D66EE" strokeWidth="1.6" strokeDasharray="3 4" data-id="element-272" data-mp="36d66l0tzp28n" />
              <circle cx="4" cy="3" r="3" fill="#4F46E5" data-id="element-273" data-mp="2smarh6swdowm" />
              <circle cx="56" cy="76" r="3" fill="#4F46E5" data-id="element-274" data-mp="2sjokp9dtzdfg" />
            </svg>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.15,
            ease: EASE
          }} className="absolute top-[235px] left-[40%] w-[37%] z-10" data-id="element-275" data-mp="2xjb1fssvbbt0">
              <AtnaCard isFR={isFR} data-id="element-276" data-mp="39t23bty5utj" />
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.25,
            ease: EASE
          }} className="absolute top-[170px] right-0 z-10" data-id="element-277" data-mp="3vedchcv96ipe">
              <PetPhone isFR={isFR} data-id="element-278" data-mp="81uk7edsqfrh" />
            </motion.div>
          </div>

          {/* Mobile / tablet stack */}
          <div className="xl:hidden space-y-5" data-id="element-279" data-mp="3gj75amkf8duh">
            <img src={ANIMALS_IMAGES.pets} alt={isFR ? 'Un golden retriever et un chat portant leur médaille' : 'A golden retriever and a cat wearing their tags'} className="w-full aspect-[4/3] md:aspect-[16/9] rounded-[24px] object-cover object-[35%_40%]" data-id="element-280" data-mp="3cgatpftati01" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-start" data-id="element-281" data-mp="15o0barh9n48t">
              <div className="space-y-5 min-w-0" data-id="element-282" data-mp="10qrnobdcm806">
                <CitizenQuestion isFR={isFR} data-id="element-283" data-mp="10s2vqndtu1x3" />
                <AtnaCard isFR={isFR} data-id="element-284" data-mp="18rfseil9vlye" />
              </div>
              <div className="flex justify-center" data-id="element-285" data-mp="18t0ls4vuoyuo">
                <PetPhone isFR={isFR} data-id="element-286" data-mp="1cqyehhoty32t" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits on the white wave */}
      <div className="relative bg-white" data-id="element-287" data-mp="1mqfky1bwokzu">
        <ul className="max-w-[1680px] mx-auto px-6 pt-10 xl:pt-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x divide-[#E8E8F0]" data-id="element-288" data-mp="1mp4fzy6q5775">
          {animalsBenefits.map(b => {
          const Icon = BENEFIT_ICONS[b.key];
          return <li key={b.key} className="flex items-start gap-4 lg:px-8 first:lg:pl-2" data-id="element-289" data-mp="z0cwdv779x5z">
                <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-290" data-mp="13rben0br15bv">
                  <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-291" data-mp="6be80gchhys8" />
                </span>
                <span className="min-w-0 pt-1" data-id="element-292" data-mp="b9z9ej3h2oi9">
                  <span className="block font-sans text-[17px] font-semibold text-[#121A3A]" data-id="element-293" data-mp="3rraot82laae">{pick(b.title)}</span>
                  <span className="block font-sans text-[15px] text-[#5B6478] leading-snug mt-1" data-id="element-294" data-mp="3uxj0tslukmx7">{pick(b.body)}</span>
                </span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}